import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form';
import type { CompanyFormData } from '../../schemas/companyInfoSchema';
import { textFieldStyles } from './sharedStyles';

interface Props {
  control: Control<CompanyFormData>;
  errors: FieldErrors<CompanyFormData>;
  isRestricted: boolean;
  isClientSettingsDisabled: boolean;
  timezones: string[];
}

export const CompanyInformationSection: React.FC<Props> = ({
  control,
  errors,
  isRestricted,
  isClientSettingsDisabled,
  timezones,
}) => {
  const theme = useTheme();

  return (
    <Grid>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        Company Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="clientName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isClientSettingsDisabled}
                fullWidth
                label="Company Name"
                sx={textFieldStyles(theme)}
                error={!!errors.clientName}
                helperText={errors.clientName?.message || `${field.value?.length || 0}/100`}
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="company_legal_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Company Legal Name"
                sx={textFieldStyles(theme)}
                error={!!errors.company_legal_name}
                helperText={errors.company_legal_name?.message || `${field.value?.length || 0}/100`}
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="company_registration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Primary business registration (ABN, etc.)"
                sx={textFieldStyles(theme)}
                error={!!errors.company_registration}
                helperText={
                  errors.company_registration?.message ||
                  `e.g., ABN (Australia), NZBN (New Zealand), VAT (United Kingdom) | ${field.value?.length || 0}/50`
                }
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="secondary_company_registration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Secondary business registration (ACN, etc.)"
                sx={textFieldStyles(theme)}
                error={!!errors.secondary_company_registration}
                helperText={
                  errors.secondary_company_registration?.message ||
                  `e.g., ACN (Australia), SC (Scotland) | ${field.value?.length || 0}/50`
                }
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="company_website"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Company Website"
                placeholder="www."
                sx={textFieldStyles(theme)}
                error={!!errors.company_website}
                helperText={errors.company_website?.message || `${field.value?.length || 0}/255`}
                inputProps={{ maxLength: 255 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="company_phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Company Phone"
                sx={textFieldStyles(theme)}
                error={!!errors.company_phone}
                helperText={errors.company_phone?.message || `${field.value?.length || 0}/50`}
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="timezone"
            control={control}
            render={({ field: { value, onChange, ref, ...fieldProps } }) => (
              <Autocomplete
                {...fieldProps}
                options={timezones}
                value={value || null}
                onChange={(_event, newValue) => onChange(newValue)}
                renderInput={(params) => {
                  const { size: _size, ...restParams } = params;
                  return (
                    <TextField
                      {...restParams}
                      disabled={isRestricted}
                      label="Timezone"
                      inputRef={ref}
                      fullWidth
                      sx={textFieldStyles(theme)}
                      error={!!errors.timezone}
                      helperText={
                        errors.timezone?.message || "Select your company's primary timezone"
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="company_address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Company Address"
                multiline
                rows={6}
                sx={textFieldStyles(theme)}
                error={!!errors.company_address}
                helperText={errors.company_address?.message || `${field.value?.length || 0}/500`}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
